import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from '../../axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import './LoginForm.scss';
import logo from '../../img/nekrodruk-logo-dark.png';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Stack,
  Alert,
} from '@mui/material';
import PasswordTextField from '../PasswordTextField';
import { getApiErrorMessage } from '../../utils/notify';
import { emailSchema } from '../../utils/validation';



export default function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');

  // Po zalogowaniu wracamy na stronę, z której ProtectedRoute przekierował na logowanie
  const fromPath = location.state?.from?.pathname;
  const returnPath = fromPath && fromPath !== '/login' ? fromPath : '/personForm';

  const { isLoggedIn, setIsLoggedIn, refreshAuth, needsConsents } = useAuth();
  // Jedyne miejsce nawigacji po zalogowaniu (także gdy zalogowana osoba wejdzie na /login)
  useEffect(() => {
    if (isLoggedIn) {
      navigate(needsConsents ? '/consents' : returnPath, { replace: true });
    }
  }, [isLoggedIn, needsConsents, navigate, returnPath]);

  const initialValues = {
    login: '',
    password: '',
  };

  const validationSchema = Yup.object({
    login: emailSchema,
    password: Yup.string().required('Hasło jest wymagane'),
  });

  const handleLogin = async (values, { setSubmitting }) => {
    const { login, password } = values;
    setError('');
    try {
      const response = await axios.post('/login', {
        login,
        password
      });
      const token = response.data.token;
      localStorage.setItem('token', token);

      try {
        // Najpierw dane konta (zgody, subskrypcja), potem przełączenie na zalogowanego – nawiguje efekt powyżej
        await refreshAuth();
      } catch (refreshError) {
        console.error('Nie udało się odświeżyć danych użytkownika po zalogowaniu:', refreshError);
      }
      setIsLoggedIn(true);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Nie udało się zalogować. Spróbuj ponownie.', {
        400: 'Niepoprawny adres e-mail lub hasło.',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className='LoginForm'>
      <Paper elevation={0} className='formCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={logo} alt='Nekrodruk' className='logo' />
          <Typography component='h1' variant='h4' className='formTitle'>
            Logowanie
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({ isSubmitting }) => (
              <Form className='formContainer' noValidate>
                <Stack spacing={2} width='100%'>
                  <Field name='login'>
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        type='email'
                        label='Adres e-mail'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  <Field name='password'>
                    {({ field, meta }) => (
                      <PasswordTextField
                        {...field}
                        label='Hasło'
                        variant='outlined'
                        fullWidth
                        error={Boolean(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error ? meta.error : ''}
                      />
                    )}
                  </Field>
                  {error && (
                    <Alert severity='error' className='formAlert'>
                      {error}
                    </Alert>
                  )}
                  <Button
                    type='submit'
                    variant='contained'
                    size='large'
                    disabled={isSubmitting}
                    className='formSubmit'
                  >
                    {isSubmitting ? 'Logowanie…' : 'Zaloguj się'}
                  </Button>
                  <Link component={RouterLink} to='/forgot-password' align='center'>
                    Nie pamiętasz hasła?
                  </Link>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Paper>
    </Box>
  );
};

